import {useMemo, useRef} from 'react';
import {Navigate, Outlet, useParams, useSearchParams} from 'react-router-dom';
import {EmblaCarouselType} from 'embla-carousel';

import {closeWebView} from '@zzng/common/scheme/web';
import {openLandingUrl} from '@zzng/common/utils/openLandingUrl';

import CardCarousel from '@/root/components/CardCarousel';
import CardExpiredNotice from '@/root/components/CardExpiredNotice';
import CardTemplate from '@/root/components/CardTemplate';
import CardUtilButtons from '@/root/components/CardUtilButtons';
import {ROUTES} from '@/root/constants/routes';
import CardGroupSkeleton from '@/root/pages/detail/:groupCode/skeleton';
import {useCardGroup} from '@/root/queries/cards';
import {useEmblaStore} from '@/root/stores/cardCarousel';

import PageLayout, {PageDescription} from '@/shared/components/PageLayout';
import {PAGE_NAME, STATUS_BAR_COLOR} from '@/shared/constants/statusBarColor';
import {TIARA_DETAIL_ACTION, TIARA_DETAIL_PAGE_NAME} from '@/shared/constants/tiara';
import withSuspense from '@/shared/hoc/withSuspense';
import useAdvancedNavigate from '@/shared/hooks/useAdvancedNavigate';
import useHistoryBackAOS from '@/shared/hooks/useHistoryBackAOS';
import useTiara from '@/shared/hooks/useTiara';

import {useUiStore} from '@/apps/root/stores/ui';

export interface CardGroupPageOutletContext {
  selectCard: (index: number) => void;
}

const CardGroupPage = () => {
  const navigate = useAdvancedNavigate();
  const [searchParams] = useSearchParams();
  const {groupCode = ''} = useParams<{groupCode: string}>();
  const {trackEvent} = useTiara({pageName: TIARA_DETAIL_PAGE_NAME.CARD_DETAIL, customProps: {group_code: groupCode}});
  const {
    data: {group, cards}
  } = useCardGroup(groupCode);
  const emblaApi = useEmblaStore((state) => state.emblaApi);
  const isFromHome = useUiStore((state) => state.isFromHome);
  const initCardIndex = useMemo(() => {
    const index = cards.findIndex((card) => card.cardId === searchParams.get('initialCardId'));
    return index === -1 ? 0 : index;
  }, [cards, searchParams]);
  const hasUtilButtons = useMemo(() => {
    return cards.find((card) => card.buttons.length);
  }, [cards]);
  const cardCarouselKey = useMemo(() => cards.map((card) => card.cardId).join(','), [cards]);
  const currentIndex = useRef(initCardIndex);

  const sortedGroupOptions = useMemo(
    () => [
      ...group.actions
        .sort((a, b) => a.sequence - b.sequence)
        .map((action) => ({
          label: action.name,
          onClick: () => {
            openLandingUrl(action.landingUrl);
            trackEvent({
              pageName: TIARA_DETAIL_PAGE_NAME.CARD_DETAIL_GROUP_ACTION,
              action: TIARA_DETAIL_ACTION.GROUP_ACTION_MENU_CLICK,
              customProps: {button_name: action.name}
            });
          }
        })),
      // NOTE: 카드 삭제는 모든 카드 그룹의 디폴트 옵션
      {
        label: '카드 삭제',
        onClick: () => {
          trackEvent({
            pageName: TIARA_DETAIL_PAGE_NAME.CARD_DETAIL_GROUP_ACTION,
            action: TIARA_DETAIL_ACTION.GROUP_ACTION_MENU_CLICK,
            customProps: {button_name: '카드 삭제'}
          });
          navigate(ROUTES.CARD_GROUP_DELETE, {pathParams: {groupCode}});
        }
      }
    ],
    [] // NOTE: actions는 안 변함
  );

  const onClickDetail = (cardId: string, index: number) => () => {
    navigate(ROUTES.CARD_DETAIL, {pathParams: {groupCode, cardId}});
    emblaApi?.scrollTo(index);
    const cardName = cards[index]?.overview.title;
    trackEvent({
      action: TIARA_DETAIL_ACTION.CARD_CLICK,
      customProps: {card_name: cardName}
    });
  };

  const onClickList = () => {
    navigate(ROUTES.CARD_LIST, {pathParams: {groupCode}});
    trackEvent({action: TIARA_DETAIL_ACTION.CARD_LIST_CLICK});
  };

  const onClickClose = () => {
    if (isFromHome) {
      navigate(ROUTES.HOME, {replace: true, transitionDirection: 'back'});
      return;
    }

    closeWebView();
  };

  const onSwipeCard = (emblaApi: EmblaCarouselType) => {
    const selectedCardIndex = emblaApi.selectedScrollSnap();

    if (currentIndex.current !== selectedCardIndex) {
      const action =
        selectedCardIndex > currentIndex.current ? TIARA_DETAIL_ACTION.RIGHT_SWIPE : TIARA_DETAIL_ACTION.LEFT_SWIPE;
      const cardName = cards[selectedCardIndex]?.overview.title ?? '';
      trackEvent({
        action,
        customProps: {card_name: cardName}
      });
      currentIndex.current = selectedCardIndex;
    }
  };

  const onExpandedComboBox = () => {
    const cardName = cards[currentIndex.current]?.overview.title ?? '';

    trackEvent({
      pageName: TIARA_DETAIL_PAGE_NAME.CARD_DETAIL_GROUP_ACTION,
      action: TIARA_DETAIL_ACTION.GROUP_ACTION_MENU_VIEW,
      customProps: {card_name: cardName}
    });
  };

  // 헤더 닫기 버튼과 동일하게 동작
  useHistoryBackAOS(onClickClose);

  if (cards.length === 0) {
    return <Navigate to={ROUTES.CARD_NOT_FOUND} replace />;
  }

  return (
    <>
      <PageLayout
        tiara={{
          pageName: TIARA_DETAIL_PAGE_NAME.CARD_DETAIL,
          pageAction: TIARA_DETAIL_ACTION.CARD_VIEW,
          customProps: {card_name: cards[initCardIndex]?.overview.title ?? ''},
          initCustomProps: {group_code: groupCode}
        }}
        className="container-card"
        statusBarColor={{default: STATUS_BAR_COLOR[PAGE_NAME.CARD_GROUP]}}
        pageDescription={PageDescription.DETAIL}
        title={group.name}
        headerProps={{
          title: group.name,
          buttons: [
            {type: 'CLOSE', onClick: onClickClose},
            {type: 'COMBOBOX', options: sortedGroupOptions, onExpanded: onExpandedComboBox}
          ]
        }}
      >
        <div className="area_card">
          <div className="group_card">
            <CardExpiredNotice />
            {/* 카드 삭제, 재발급 등으로 보유한 카드가 변경되면 다시 mount 한다. */}
            <CardCarousel key={cardCarouselKey} cards={cards} startIndex={initCardIndex} onSwipe={onSwipeCard}>
              {(card, index, selectedCardIndex) => (
                <CardTemplate
                  card={card}
                  onClickDetail={onClickDetail(card.cardId, index)}
                  isActive={selectedCardIndex === index}
                />
              )}
            </CardCarousel>
          </div>
          {hasUtilButtons && <CardUtilButtons />}
        </div>
        {cards.length > 1 && (
          <button type="button" className="btn_list" onClick={onClickList}>
            <span className="icon icon_list">카드 목록</span>
          </button>
        )}
      </PageLayout>
      <Outlet />
    </>
  );
};

export default withSuspense(CardGroupPage, CardGroupSkeleton);
